import React, { useState } from 'react';
import { Storage } from 'aws-amplify';
import operationData from '../../../../../Operation/data.json';
import FoodDeliveryTabs from './FoodDeliveryTabs';

const Notifications = () => {
  const institution = operationData.foodDeliveryInstitution;
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setSelectedImage(file);
    await uploadImageToS3(file);
  };

  const uploadImageToS3 = async (file) => {
    try {
      setIsUploading(true);
      setError('');

      const fileExtension = file.name?.split('.').pop() || 'jpg';
      const fileName = `notifications/${institution}/${Date.now()}.${fileExtension}`;

      const result = await Storage.put(fileName, file, {
        contentType: file.type || 'image/jpeg',
        level: 'public',
      });

      const presignedUrl = await Storage.get(result.key, { level: 'public' });
      const cleanUrl = presignedUrl.split('?')[0];

      setImageUrl(cleanUrl);
      setMessage('✓ Image uploaded to S3 successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Failed to upload image to S3: ' + (err?.message || ''));
      setSelectedImage(null);
      setImageUrl('');
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImageUrl('');
  };

  const handleSendNotification = async () => {
    if (!title.trim()) {
      setError('Please enter a notification title');
      return;
    }

    setIsSending(true);
    setError('');
    setMessage('');

    try {
      // This is a placeholder since actual server-side implementation is required
      // In production, this would call an API endpoint to send the notification via FCM
      console.log('Notification to send:', {
        title: title.trim(),
        body: description.trim(),
        image: imageUrl,
        timestamp: new Date().toISOString(),
      });

      setMessage('✓ Notification would be sent to all users! (Server implementation required)');
      setTitle('');
      setDescription('');
      setImageUrl('');
      setSelectedImage(null);
    } catch (err) {
      setError('Failed to send notification: ' + (err?.message || ''));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-3xl space-y-6">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Send Notification</h1>
            <p className="mt-1 text-sm text-gray-500">Broadcast notifications to all app users in real-time</p>
          </div>

          {/* Messages */}
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}
          {message && (
            <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
              {message}
            </div>
          )}

          {/* Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Notification Title *</label>
              <input
                type="text"
                placeholder="Enter an engaging title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Description (Optional)</label>
              <textarea
                placeholder="Write detailed message content..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Promotional Image (Optional)</label>
              {selectedImage && imageUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-300 h-48 bg-gray-100">
                  <img
                    src={imageUrl}
                    alt="Notification preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition">
                    <button
                      onClick={removeImage}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                    >
                      Remove
                    </button>
                    <label className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 cursor-pointer">
                      Change
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={isUploading}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <label className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition">
                  <div className="text-4xl mb-2">🖼️</div>
                  <div className="text-sm font-semibold text-gray-900">Add Promotional Image</div>
                  <div className="text-xs text-gray-500">JPG, PNG (Max 5MB)</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="hidden"
                  />
                </label>
              )}
              {isUploading && (
                <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                  <span className="animate-spin">⏳</span> Uploading image...
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleSendNotification}
                disabled={isSending || isUploading || !title.trim()}
                className="flex-1 rounded-lg bg-gray-900 text-white px-4 py-2.5 font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {isSending ? 'Sending...' : '📨 Send to All App Holders'}
              </button>
              <button
                onClick={() => {
                  setTitle('');
                  setDescription('');
                  removeImage();
                  setError('');
                  setMessage('');
                }}
                disabled={isSending || isUploading}
                className="rounded-lg bg-gray-200 text-gray-900 px-4 py-2.5 font-semibold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">ℹ️ How It Works</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Title is required; description and image are optional</li>
              <li>• Notifications are sent via Firebase Cloud Messaging (FCM)</li>
              <li>• All app users will receive the notification in real-time</li>
              <li>• Image is uploaded to AWS S3 for secure delivery</li>
              <li>• Server-side implementation required for full functionality</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
